import React, { Component } from 'react';
import { connect } from 'react-redux';
import axiosInstance from '../../main/axios';
import { toastr } from 'react-redux-toastr';
import { prepararAnexoDeArquivo, ehAnexoPdf } from './ordemServicoAnexoUtils';

const TIPO_LABELS = {
  ABERTURA: 'Abertura',
  COMENTARIO: 'Comentário',
  ATUALIZACAO: 'Atualização',
  LAUDO: 'Laudo',
  CORRECAO: 'Correção',
};

const TIPO_OPCOES = [
  { value: 'COMENTARIO', label: 'Comentário', icon: 'comment' },
  { value: 'ATUALIZACAO', label: 'Atualização', icon: 'sync-alt' },
  { value: 'LAUDO', label: 'Laudo', icon: 'file-medical-alt' },
  { value: 'CORRECAO', label: 'Correção', icon: 'eraser' },
];

const PLACEHOLDER_POR_TIPO = {
  COMENTARIO: 'Descreva observações, retornos de campo ou anotações gerais...',
  ATUALIZACAO: 'Informe o andamento, etapa concluída ou mudança de status do atendimento...',
  LAUDO: 'Resumo opcional — o laudo completo pode ser anexado em PDF.',
  CORRECAO: 'Explique o que está sendo corrigido (ex.: foto errada, laudo substituído, anexo duplicado)...',
};

const HINT_POR_TIPO = {
  COMENTARIO: 'Registro livre para observações da equipe.',
  ATUALIZACAO: 'Marca o progresso da OS — ideal para mudanças de etapa.',
  LAUDO: 'Documento técnico formal — pode ser texto resumido e/ou PDF do laudo assinado.',
  CORRECAO: 'Gera um novo registro no histórico — remova anexos incorretos e/ou envie substitutos.',
};

const TIPO_BADGE_CLASS = {
  ABERTURA: 'os-interacao-tipo-badge--abertura',
  COMENTARIO: 'os-interacao-tipo-badge--comentario',
  ATUALIZACAO: 'os-interacao-tipo-badge--atualizacao',
  LAUDO: 'os-interacao-tipo-badge--laudo',
  CORRECAO: 'os-interacao-tipo-badge--correcao',
};

function anexosAtivosDaOs(interacoes) {
  return interacoes.flatMap(interacao =>
    (interacao.anexos || []).map(anexo => ({
      ...anexo,
      interacao_id_origem: interacao.id,
      interacao_tipo_origem: interacao.tipo_interacao,
      interacao_data_origem: interacao.data_interacao,
      interacao_usuario_origem: interacao.nome_usuario,
    })),
  );
}

function iniciais(nome) {
  if (!nome) return '?';
  return nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(parte => parte[0])
    .join('')
    .toUpperCase();
}

function formatarDataInteracao(valor) {
  if (!valor) return '';
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

class OrdemServicoInteracoes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      interacoes: [],
      texto: '',
      tipo: 'COMENTARIO',
      loading: false,
      enviando: false,
      anexosPendentes: [],
      anexosRemover: [],
      processandoFotos: false,
      anexoUrls: {},
      anexoCarregando: {},
      anexoMetas: {},
      anexoAmpliado: null,
    };
    this.fileInputRef = React.createRef();
    this.carregar = this.carregar.bind(this);
    this.enviar = this.enviar.bind(this);
    this.selecionarFotos = this.selecionarFotos.bind(this);
    this.removerAnexoPendente = this.removerAnexoPendente.bind(this);
    this.toggleAnexoRemover = this.toggleAnexoRemover.bind(this);
    this.iniciarCorrecaoInteracao = this.iniciarCorrecaoInteracao.bind(this);
    this.alterarTipo = this.alterarTipo.bind(this);
    this.abrirSeletorFotos = this.abrirSeletorFotos.bind(this);
    this.carregarUrlAnexo = this.carregarUrlAnexo.bind(this);
    this.abrirAnexo = this.abrirAnexo.bind(this);
    this.fecharAmpliado = this.fecharAmpliado.bind(this);
  }

  componentDidMount() {
    this.carregar();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.osId !== this.props.osId) {
      this.revogarUrlsAnexos();
      this.setState({ anexosPendentes: [], anexosRemover: [], anexoAmpliado: null, texto: '', tipo: 'COMENTARIO' });
      this.carregar();
    }
  }

  componentWillUnmount() {
    this.revogarUrlsAnexos();
  }

  revogarUrlsAnexos() {
    const { anexoUrls } = this.state;
    Object.values(anexoUrls).forEach(url => {
      if (url) window.URL.revokeObjectURL(url);
    });
  }

  carregar() {
    const { osId } = this.props;
    if (!osId) return;

    this.setState({ loading: true });
    axiosInstance
      .get(`/manutencao/ordens_servico/${osId}/interacoes`)
      .then(resp => {
        const interacoes = resp.data || [];
        this.setState({ interacoes, loading: false }, () => {
          interacoes.forEach(interacao => {
            (interacao.anexos || []).forEach(anexo => {
              this.carregarUrlAnexo(anexo);
            });
          });
        });
      })
      .catch(() => {
        toastr.error('Erro', 'Não foi possível carregar as interações da OS.');
        this.setState({ loading: false });
      });
  }

  abrirSeletorFotos() {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  }

  async selecionarFotos(event) {
    const arquivos = Array.from(event.target.files || []);
    event.target.value = '';

    if (!arquivos.length) return;

    this.setState({ processandoFotos: true });

    try {
      const novos = [];
      const { tipo } = this.state;
      for (const arquivo of arquivos) {
        const anexo = await prepararAnexoDeArquivo(arquivo, {
          permitePdf: tipo === 'LAUDO' || tipo === 'CORRECAO',
          tipoAnexo: tipo === 'LAUDO' ? 'LAUDO' : 'FOTO',
        });
        novos.push(anexo);
      }

      this.setState(prev => ({
        anexosPendentes: [...prev.anexosPendentes, ...novos],
        processandoFotos: false,
      }));
    } catch (error) {
      toastr.warning('Atenção', error.message || 'Não foi possível processar a imagem.');
      this.setState({ processandoFotos: false });
    }
  }

  removerAnexoPendente(anexoId) {
    this.setState(prev => ({
      anexosPendentes: prev.anexosPendentes.filter(a => a.id !== anexoId),
    }));
  }

  alterarTipo(tipo) {
    this.setState(prev => ({
      tipo,
      anexosRemover: tipo === 'CORRECAO' ? prev.anexosRemover : [],
    }));
  }

  toggleAnexoRemover(anexoId) {
    this.setState(prev => {
      const selecionados = prev.anexosRemover.includes(anexoId)
        ? prev.anexosRemover.filter(id => id !== anexoId)
        : [...prev.anexosRemover, anexoId];
      return { anexosRemover: selecionados };
    });
  }

  iniciarCorrecaoInteracao(interacao) {
    const ids = (interacao.anexos || []).map(anexo => anexo.id);
    this.setState({
      tipo: 'CORRECAO',
      anexosRemover: ids,
      texto: ids.length
        ? `Correção dos anexos da interação de ${formatarDataInteracao(interacao.data_interacao)}.`
        : '',
    });
    const composer = document.querySelector('.os-interacao-composer');
    if (composer) composer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async carregarUrlAnexo(anexoOuId) {
    const { osId } = this.props;
    const { anexoUrls, anexoCarregando } = this.state;
    const anexo =
      typeof anexoOuId === 'object'
        ? anexoOuId
        : { id: anexoOuId, mime_type: this.state.anexoMetas[anexoOuId] };
    const anexoId = anexo.id;

    if (!osId || !anexoId || anexoUrls[anexoId] || anexoCarregando[anexoId]) {
      return anexoUrls[anexoId] || null;
    }

    this.setState(prev => ({
      anexoCarregando: { ...prev.anexoCarregando, [anexoId]: true },
    }));

    try {
      const response = await axiosInstance.get(
        `/manutencao/ordens_servico/${osId}/anexos/${anexoId}/download`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(response.data);
      this.setState(prev => ({
        anexoUrls: { ...prev.anexoUrls, [anexoId]: url },
        anexoCarregando: { ...prev.anexoCarregando, [anexoId]: false },
        anexoMetas: {
          ...prev.anexoMetas,
          [anexoId]: response.headers['content-type'] || anexo.mime_type,
        },
      }));
      return url;
    } catch (error) {
      this.setState(prev => ({
        anexoCarregando: { ...prev.anexoCarregando, [anexoId]: false },
      }));
      return null;
    }
  }

  async abrirAnexo(anexo) {
    const url = this.state.anexoUrls[anexo.id] || (await this.carregarUrlAnexo(anexo));
    if (!url) return;

    if (ehAnexoPdf(anexo)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    this.setState({ anexoAmpliado: { ...anexo, url } });
  }

  fecharAmpliado() {
    this.setState({ anexoAmpliado: null });
  }

  enviar() {
    const { osId } = this.props;
    const { texto, tipo, anexosPendentes, anexosRemover } = this.state;
    const textoLimpo = texto ? texto.trim() : '';
    const possuiAnexos = anexosPendentes.length > 0;
    const possuiRemocoes = anexosRemover.length > 0;
    const ehCorrecao = tipo === 'CORRECAO';

    if (ehCorrecao) {
      if (!textoLimpo && !possuiAnexos && !possuiRemocoes) {
        toastr.warning(
          'Atenção',
          'Informe o motivo da correção, selecione anexos para remover ou anexe substitutos.',
        );
        return;
      }
    } else if (!textoLimpo && !possuiAnexos) {
      toastr.warning('Atenção', 'Digite o texto da interação ou anexe ao menos uma foto.');
      return;
    }

    this.setState({ enviando: true });
    axiosInstance
      .post(`/manutencao/ordens_servico/${osId}/interacoes`, {
        texto_interacao: textoLimpo,
        tipo_interacao: tipo,
        anexos_remover: ehCorrecao ? anexosRemover : [],
        anexos: anexosPendentes.map(({ imagem_base64, mime_type, tamanho_bytes, nome_original, tipo_anexo }) => ({
          imagem_base64,
          mime_type,
          tamanho_bytes,
          nome_original,
          tipo_anexo,
        })),
      })
      .then(() => {
        const msg = ehCorrecao
          ? 'Correção registrada no histórico.'
          : possuiAnexos
            ? 'Interação e fotos registradas.'
            : 'Interação registrada.';
        toastr.success('Sucesso', msg);
        this.setState({ texto: '', anexosPendentes: [], anexosRemover: [], enviando: false, tipo: 'COMENTARIO' });
        this.carregar();
      })
      .catch(e => {
        const msg = e.response && e.response.data && e.response.data.message;
        toastr.error('Erro', msg || 'Não foi possível registrar a interação.');
        this.setState({ enviando: false });
      });
  }

  renderTipoSelector() {
    const { tipo } = this.state;

    return (
      <div className="os-interacao-tipo-pills">
        <span className="os-interacao-field-label">Tipo de interação</span>
        <div className="os-interacao-tipo-pills-row">
          {TIPO_OPCOES.map(opcao => (
            <button
              key={opcao.value}
              type="button"
              className={`os-interacao-tipo-pill ${tipo === opcao.value ? 'is-active' : ''}`}
              onClick={() => this.alterarTipo(opcao.value)}
            >
              <i className={`fa fa-${opcao.icon}`} />
              {opcao.label}
            </button>
          ))}
        </div>
        <p className="os-interacao-tipo-hint">{HINT_POR_TIPO[tipo]}</p>
      </div>
    );
  }

  renderSeletorAnexosRemover() {
    const { interacoes, anexosRemover } = this.state;
    const anexosDisponiveis = anexosAtivosDaOs(interacoes);

    if (!anexosDisponiveis.length) {
      return (
        <div className="os-interacao-correcao-empty">
          <i className="fa fa-info-circle" />
          Não há anexos ativos nesta OS para remover.
        </div>
      );
    }

    return (
      <div className="os-interacao-correcao-anexos">
        <span className="os-interacao-field-label">Anexos a remover do histórico visível</span>
        <p className="os-interacao-correcao-hint">
          Os itens marcados deixam de aparecer nas interações anteriores, mas ficam registrados nesta correção.
        </p>
        <div className="os-interacao-correcao-lista">
          {anexosDisponiveis.map(anexo => {
            const selecionado = anexosRemover.includes(anexo.id);
            return (
              <label key={anexo.id} className={`os-interacao-correcao-item ${selecionado ? 'is-selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selecionado}
                  onChange={() => this.toggleAnexoRemover(anexo.id)}
                />
                <span className="os-interacao-correcao-item-icon">
                  <i className={`fa ${ehAnexoPdf(anexo) ? 'fa-file-pdf' : 'fa-image'}`} />
                </span>
                <span className="os-interacao-correcao-item-body">
                  <strong>{anexo.nome_original || (ehAnexoPdf(anexo) ? 'Documento PDF' : 'Foto')}</strong>
                  <small>
                    {TIPO_LABELS[anexo.interacao_tipo_origem] || 'Interação'} ·{' '}
                    {formatarDataInteracao(anexo.interacao_data_origem)} · {anexo.interacao_usuario_origem}
                  </small>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  renderAnexosRemovidosHistorico(anexosRemovidos) {
    if (!anexosRemovidos || anexosRemovidos.length === 0) return null;

    return (
      <div className="os-interacao-anexos-removidos">
        <span className="os-interacao-anexos-removidos-label">
          <i className="fa fa-ban" /> Anexos removidos nesta correção
        </span>
        <ul>
          {anexosRemovidos.map(anexo => (
            <li key={anexo.id}>
              <i className={`fa ${ehAnexoPdf(anexo) ? 'fa-file-pdf' : 'fa-image'}`} />
              {anexo.nome_original || (ehAnexoPdf(anexo) ? 'PDF' : 'Foto')}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderAnexosInteracao(anexos) {
    const { anexoUrls, anexoCarregando } = this.state;
    if (!anexos || anexos.length === 0) return null;

    return (
      <div className="os-interacao-anexos-grid">
        {anexos.map(anexo => {
          const url = anexoUrls[anexo.id];
          const carregando = anexoCarregando[anexo.id];

          return (
            <button
              key={anexo.id}
              type="button"
              className={`os-interacao-anexo-thumb ${ehAnexoPdf(anexo) ? 'is-pdf' : ''}`}
              onClick={() => this.abrirAnexo(anexo)}
              title={anexo.nome_original || (ehAnexoPdf(anexo) ? 'Abrir PDF' : 'Ver foto')}
            >
              {ehAnexoPdf(anexo) ? (
                <span className="os-interacao-anexo-pdf">
                  <i className="fa fa-file-pdf" />
                  <span>PDF</span>
                </span>
              ) : url ? (
                <img src={url} alt={anexo.nome_original || 'Foto da OS'} />
              ) : (
                <span className="os-interacao-anexo-placeholder">
                  <i className={`fa ${carregando ? 'fa-spinner fa-spin' : 'fa-image'}`} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  renderAnexosPendentes() {
    const { anexosPendentes } = this.state;
    if (!anexosPendentes.length) return null;

    return (
      <div className="os-interacao-anexos-pendentes">
        <span className="os-interacao-anexos-label">
          <i className="fa fa-paperclip" /> {anexosPendentes.length} anexo(s) pronto(s) para envio
        </span>
        <div className="os-interacao-anexos-grid">
          {anexosPendentes.map(anexo => (
            <div key={anexo.id} className="os-interacao-anexo-pendente">
              {ehAnexoPdf(anexo) ? (
                <div className="os-interacao-anexo-pdf os-interacao-anexo-pdf--pendente">
                  <i className="fa fa-file-pdf" />
                  <span className="os-cell-ellipsis">{anexo.nome_original || 'Laudo.pdf'}</span>
                </div>
              ) : (
                <img src={anexo.preview} alt={anexo.nome_original || 'Pré-visualização'} />
              )}
              <button
                type="button"
                className="os-interacao-anexo-remover"
                onClick={() => this.removerAnexoPendente(anexo.id)}
                title="Remover foto"
              >
                <i className="fa fa-times" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderHistorico() {
    const { interacoes, loading } = this.state;

    if (loading) {
      return (
        <div className="os-interacao-loading">
          <i className="fa fa-spinner fa-spin" />
          <span>Carregando interações...</span>
        </div>
      );
    }

    if (interacoes.length === 0) {
      return (
        <div className="os-interacao-empty">
          <div className="os-interacao-empty-icon">
            <i className="fa fa-comments" />
          </div>
          <strong>Nenhuma interação registrada ainda</strong>
          <p>Registre comentários, atualizações de andamento ou laudos técnicos com fotos.</p>
        </div>
      );
    }

    return (
      <div className="os-interacao-timeline">
        {interacoes.map(i => (
          <article key={i.id} className="os-interacao-item">
            <div className="os-interacao-item-avatar" aria-hidden="true">
              {iniciais(i.nome_usuario)}
            </div>
            <div className="os-interacao-item-body">
              <div className="os-interacao-item-header">
                <div className="os-interacao-item-meta">
                  <strong>{i.nome_usuario}</strong>
                  <span
                    className={`os-interacao-tipo-badge ${
                      TIPO_BADGE_CLASS[i.tipo_interacao] || 'os-interacao-tipo-badge--comentario'
                    }`}
                  >
                    {TIPO_LABELS[i.tipo_interacao] || i.tipo_interacao}
                  </span>
                </div>
                <time className="os-interacao-item-data">{formatarDataInteracao(i.data_interacao)}</time>
              </div>
              {(i.anexos || []).length > 0 && (
                <button
                  type="button"
                  className="os-interacao-corrigir-btn"
                  onClick={() => this.iniciarCorrecaoInteracao(i)}
                  title="Registrar correção destes anexos"
                >
                  <i className="fa fa-eraser" /> Corrigir anexos
                </button>
              )}
              {i.texto_interacao && i.texto_interacao !== '(sem texto)' && (
                <p className="os-interacao-item-texto">{i.texto_interacao}</p>
              )}
              {this.renderAnexosInteracao(i.anexos)}
              {this.renderAnexosRemovidosHistorico(i.anexos_removidos)}
            </div>
          </article>
        ))}
      </div>
    );
  }

  render() {
    const { osNumero } = this.props;
    const { texto, enviando, processandoFotos, anexoAmpliado, tipo } = this.state;
    const ehCorrecao = tipo === 'CORRECAO';
    const permitePdf = tipo === 'LAUDO' || ehCorrecao;
    const acceptAnexos = permitePdf
      ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,application/pdf,.pdf'
      : 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

    if (!this.props.osId) {
      return (
        <div className="os-interacoes-premium os-interacoes-empty-selection">
          <i className="fa fa-hand-pointer" />
          <p>Selecione uma OS na listagem para ver e registrar interações.</p>
        </div>
      );
    }

    return (
      <div className="os-interacoes-premium simple-cad-form-premium">
        <div className="simple-cad-premium-container">
          <div className="simple-cad-premium-card">
            <div className="simple-cad-premium-header">
              <h4>
                <i className="fa fa-comments" /> Interações — {osNumero}
              </h4>
              <p>Histórico de comentários, atualizações e laudos desta ordem de serviço.</p>
            </div>

            <div className="simple-cad-premium-body">
              <section className="os-interacao-composer">
                <div className="os-interacao-composer-header">
                  <h5>
                    <i className="fa fa-edit" /> Nova interação
                  </h5>
                </div>

                {this.renderTipoSelector()}

                {ehCorrecao && this.renderSeletorAnexosRemover()}

                <div className="os-interacao-texto-wrap">
                  <label className="os-interacao-field-label" htmlFor="os-interacao-texto">
                    Descrição
                  </label>
                  <textarea
                    id="os-interacao-texto"
                    className="form-control os-interacao-textarea"
                    rows={4}
                    value={texto}
                    onChange={e => this.setState({ texto: e.target.value })}
                    placeholder={PLACEHOLDER_POR_TIPO[tipo] || PLACEHOLDER_POR_TIPO.COMENTARIO}
                  />
                </div>

                {this.renderAnexosPendentes()}

                <div className="os-interacao-composer-footer">
                  <p className="os-interacao-upload-hint">
                    <i className="fa fa-info-circle" />
                    {permitePdf
                      ? ehCorrecao
                        ? 'Remova anexos incorretos e/ou envie substitutos (fotos até 5 MB, PDF até 10 MB)'
                        : 'Fotos JPG/PNG/WEBP até 5 MB ou laudo em PDF até 10 MB'
                      : 'JPG, PNG ou WEBP · até 5 MB · câmera ou galeria no celular'}
                  </p>
                  <div className="os-interacao-composer-actions">
                    <button
                      type="button"
                      className="os-interacao-foto-btn"
                      onClick={this.abrirSeletorFotos}
                      disabled={enviando || processandoFotos}
                    >
                      <i
                        className={`fa ${
                          processandoFotos ? 'fa-spinner fa-spin' : permitePdf ? 'fa-paperclip' : 'fa-camera'
                        }`}
                      />
                      {processandoFotos
                        ? 'Processando...'
                        : permitePdf
                          ? ehCorrecao
                            ? 'Anexar substitutos'
                            : 'Anexar fotos ou PDF'
                          : 'Adicionar fotos'}
                    </button>
                    <button
                      type="button"
                      className="os-interacao-registrar-btn"
                      onClick={this.enviar}
                      disabled={enviando || processandoFotos}
                    >
                      <i className={`fa ${enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                      {enviando ? 'Enviando...' : ehCorrecao ? 'Registrar correção' : 'Registrar'}
                    </button>
                  </div>
                </div>
              </section>

              <section className="os-interacao-historico">
                <div className="os-interacao-historico-header">
                  <h5>
                    <i className="fa fa-history" /> Histórico
                  </h5>
                </div>
                {this.renderHistorico()}
              </section>
            </div>
          </div>
        </div>

        <input
          ref={this.fileInputRef}
          type="file"
          accept={acceptAnexos}
          capture={permitePdf ? undefined : 'environment'}
          multiple
          tabIndex={-1}
          aria-hidden="true"
          className="os-interacao-file-input"
          onChange={this.selecionarFotos}
        />

        {anexoAmpliado && (
          <div className="os-interacao-lightbox" onClick={this.fecharAmpliado} role="presentation">
            <div className="os-interacao-lightbox-content" onClick={e => e.stopPropagation()} role="presentation">
              <button type="button" className="os-interacao-lightbox-fechar" onClick={this.fecharAmpliado}>
                <i className="fa fa-times" />
              </button>
              <img src={anexoAmpliado.url} alt={anexoAmpliado.nome_original || 'Foto ampliada'} />
              <div className="os-interacao-lightbox-meta">
                {anexoAmpliado.nome_usuario && <span>{anexoAmpliado.nome_usuario}</span>}
                {anexoAmpliado.data_upload && (
                  <span>{formatarDataInteracao(anexoAmpliado.data_upload)}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  osId: state.manutencaoOS.selectedOS && state.manutencaoOS.selectedOS.id,
  osNumero: state.manutencaoOS.selectedOS && state.manutencaoOS.selectedOS.numero,
});

export default connect(mapStateToProps)(OrdemServicoInteracoes);
